import { Command } from 'commander';
import { readFileSync, writeFileSync, existsSync, renameSync } from 'node:fs';
import { createHash } from 'node:crypto';
import chalk from 'chalk';

import { getClient } from '../lib/api.js';
import { resolveContext } from '../lib/resolve.js';
import { requirePermission } from '../lib/permissions.js';
import * as output from '../lib/output.js';
import * as progress from '../lib/progress.js';
// One parser for every command.  previously carried its own, which
// did not understand a leading `export `, so an .envrc line became the key
// "export CLOUDFLARE_API_TOKEN" and the API rejected it as not
// UPPER_SNAKE_CASE. Two parsers meant `cf push` and `cf sync` disagreed
// about the same file.
import { applyEnvChanges, parseEnvContent, renderEnvFile, toValueMap } from '../lib/env-file.js';
import { chunk, validateSecrets } from '../lib/secret-validation.js';
import { revealSecrets } from '../lib/reveal.js';

const parseEnvFile = (content: string): Map<string, string> => toValueMap(parseEnvContent(content));
const formatEnvFile = (secrets: Map<string, string>): string =>
  renderEnvFile(secrets, `# Generated by CryptFlare CLI\n# ${new Date().toISOString()}`);

/**
 * Warns when a pulled value contains a newline.
 *
 * The writer encodes it as `KEY="line1\nline2"`, which this parser and dotenv
 * both decode back to a real newline. A POSIX shell does not: `\n` inside
 * double quotes is a literal backslash and an `n`, so `set -a; . .env` yields
 * the wrong value with no indication anything was lost. A real newline in
 * single quotes would source correctly but is exactly the multi-line form the
 * parser refuses to rewrite in place, so the encoding stays and the caller is
 * told instead. `cf env -f shell` emits a shell-correct form for these.
 */
export function findNewlineValues(values: Map<string, string>): string[] {
  return [...values.entries()].filter(([, v]) => v.includes('\n')).map(([k]) => k);
}

function warnAboutNewlineValues(values: Map<string, string>): void {
  const keys = findNewlineValues(values);
  if (keys.length === 0) return;
  output.warn(
    `${keys.length} value(s) contain newlines and are written escaped: ${keys.join(', ')}.`,
  );
  console.error(
    chalk.dim(
      `  Readable by dotenv and by \`cf\`, but sourcing the file in a shell yields a literal \\n.\n`
        + `  For shell use: ${chalk.cyan('eval "$(cf env -f shell)"')}`,
    ),
  );
}

export const pullCommand = new Command('pull')
  .description('Pull remote secrets to a local .env file')
  .option('-o, --org <id>', 'Organisation ID')
  .option('-w, --workspace <slug>', 'Workspace slug')
  .option('-e, --env <slug>', 'Environment slug')
  .option('-f, --file <path>', 'Output file path', '.env')
  .option('--overwrite', 'Replace the file wholesale instead of merging into it')
  .option('--json', 'Output as JSON instead of .env format')
  .addHelpText(
    'after',
    `
By default an existing file is MERGED into: pulled keys are updated in place,
new ones appended, and comments, ordering and any line the CLI does not manage
are preserved. Pass --overwrite to regenerate the file from scratch instead.

The file is written with mode 0600, since it holds plaintext secrets.`,
  )
  .action(async (opts) => {
    try {
      const ctx = resolveContext(opts);
      const scope = { organisation: ctx.org, workspace: ctx.workspace, environment: ctx.env };
      const client = getClient();
      progress.start('Fetching secrets...');

      // One request for the whole environment. This was a list plus one
      // reveal per key, so a 23-secret file cost 24 round trips.
      const revealed = await revealSecrets(client, scope);
      const count = revealed.size;

      if (count === 0) {
        progress.info('No secrets found in this environment.');
        return;
      }

      progress.stop();

      if (opts.json) {
        output.json(Object.fromEntries(revealed));
        return;
      }

      // 0600 because this file holds plaintext secrets. It previously used the
      // default, which on a typical umask is 0644 - readable by every other
      // user on the machine, including on a shared CI runner.
      const MODE = 0o600;

      warnAboutNewlineValues(revealed);

      if (!existsSync(opts.file) || opts.overwrite) {
        writeFileSync(opts.file, formatEnvFile(revealed), { encoding: 'utf-8', mode: MODE });
        output.success(`Pulled ${chalk.bold(String(revealed.size))} secrets to ${chalk.bold(opts.file)}`);
        return;
      }

      // Merge rather than refuse. Refusing meant the only way forward was
      // --overwrite, which discarded comments, ordering and any key the CLI
      // does not manage - so the safe-looking option was the destructive one.
      const original = readFileSync(opts.file, 'utf-8');
      const parsed = parseEnvContent(original);
      const updates = new Map<string, string>();
      const additions = new Map<string, string>();
      for (const [key, value] of revealed) {
        if (parsed.entries.has(key)) updates.set(key, value);
        else additions.set(key, value);
      }

      const { content, skippedMultiline } = applyEnvChanges(original, updates, additions);
      if (content !== original) {
        writeFileSync(opts.file, content, { encoding: 'utf-8', mode: MODE });
      }

      const changed = [...updates.keys()].filter((k) => parsed.entries.get(k)?.value !== revealed.get(k));
      output.success(
        `Merged into ${chalk.bold(opts.file)}: ` +
          `${chalk.green(`+${additions.size}`)} new, ${chalk.blue(`~${changed.length}`)} updated, ` +
          `${chalk.dim(`${updates.size - changed.length} unchanged`)}`,
      );
      if (skippedMultiline.length > 0) {
        output.warn(`Left multi-line values untouched: ${skippedMultiline.join(', ')}`);
      }
    } catch (err) {
      output.handleError(err);
    }
  });

export const pushCommand = new Command('push')
  .description('Push local .env file to remote environment')
  .option('-o, --org <id>', 'Organisation ID')
  .option('-w, --workspace <slug>', 'Workspace slug')
  .option('-e, --env <slug>', 'Environment slug')
  .option('-f, --file <path>', 'Input file path', '.env')
  .option('--dry-run', 'Show what would be pushed without making changes')
  .action(async (opts) => {
    try {
      const ctx = resolveContext(opts);
      const scope = { organisation: ctx.org, workspace: ctx.workspace, environment: ctx.env };
      const client = getClient();

      if (!existsSync(opts.file)) {
        output.warn(`File ${chalk.bold(opts.file)} not found.`);
        process.exit(1);
      }

      const content = readFileSync(opts.file, 'utf-8');
      const local = parseEnvFile(content);

      if (local.size === 0) {
        output.info('No secrets found in file.');
        return;
      }

      // Validate the whole file before writing anything. Previously each key
      // was sent individually with no rollback, so a rejection partway through
      // left the environment half-written and named only the failing field -
      // not which keys had already landed.
      const problems = validateSecrets(local);
      if (problems.length > 0) {
        console.log();
        output.warn(`${opts.file} has ${problems.length} problem(s). Nothing was pushed.`);
        console.log();
        for (const p of problems) {
          console.log(`    ${chalk.red(p.key)}  ${chalk.dim(p.reason)}`);
        }
        console.log();
        process.exit(1);
      }

      progress.start('Comparing with remote...');

      const remoteKeys = new Set<string>();
      const remotePage = await client.secrets.list(scope);
      for await (const secret of remotePage) {
        remoteKeys.add(secret.key);
      }

      const toCreate: Array<{ key: string; value: string }> = [];
      const toUpdate: Array<{ key: string; value: string }> = [];
      for (const [key, value] of local) {
        if (remoteKeys.has(key)) toUpdate.push({ key, value });
        else toCreate.push({ key, value });
      }

      progress.stop();

      if (opts.dryRun) {
        console.log();
        console.log(chalk.bold('  Dry run - no changes will be made'));
        console.log();
        if (toCreate.length > 0) {
          console.log(chalk.green(`  + ${toCreate.length} new secrets:`));
          toCreate.forEach(({ key }) => console.log(chalk.green(`    + ${key}`)));
        }
        if (toUpdate.length > 0) {
          console.log(chalk.blue(`  ~ ${toUpdate.length} secrets to update:`));
          toUpdate.forEach(({ key }) => console.log(chalk.blue(`    ~ ${key}`)));
        }
        console.log(chalk.dim(`\n  ${local.size} key(s) validated - no problems found.`));
        console.log();
        return;
      }

      progress.start(`Pushing ${local.size} secrets...`);
      const landed: string[] = [];

      // Batch endpoints instead of one request per key: each batch is a single
      // multi-row INSERT server-side, so it either lands whole or not at all.
      // It also cuts a 24-key push from 24 requests to one, which removes most
      // of the window in which a transient failure can half-apply a file.
      try {
        for (const batch of chunk(toCreate)) {
          progress.update(`Creating ${batch.length} secrets...`);
          await client.secrets.batchCreate({ ...scope, secrets: batch });
          landed.push(...batch.map((s) => s.key));
        }
        for (const batch of chunk(toUpdate)) {
          progress.update(`Updating ${batch.length} secrets...`);
          await client.secrets.batchUpdate({ ...scope, secrets: batch });
          landed.push(...batch.map((s) => s.key));
        }
      } catch (err) {
        progress.stop();
        // Say what did land. A bare error leaves the caller unable to tell an
        // untouched environment from a partly-written one.
        if (landed.length > 0) {
          output.warn(`Partially applied: ${landed.length} of ${local.size} key(s) were written before the failure.`);
          console.log(chalk.dim(`    ${landed.join(', ')}`));
          console.log(chalk.dim('    Re-running this push is safe - it is idempotent.'));
          console.log();
        } else {
          output.warn('Nothing was written.');
        }
        throw err;
      }

      progress.stop();
      output.success(`Pushed ${chalk.bold(String(landed.length))} secrets (${chalk.green(`+${toCreate.length}`)} new, ${chalk.blue(`~${toUpdate.length}`)} updated)`);
    } catch (err) {
      output.handleError(err);
    }
  });

export const diffCommand = new Command('diff')
  .description('Compare local .env file with remote secrets')
  .option('-o, --org <id>', 'Organisation ID')
  .option('-w, --workspace <slug>', 'Workspace slug')
  .option('-e, --env <slug>', 'Environment slug')
  .option('-f, --file <path>', 'Input file path', '.env')
  .option('--values', 'Also compare values, not just key names (decrypts each shared key)')
  .option('--exit-code', 'Exit 1 when anything differs, for use in CI')
  .action(async (opts) => {
    try {
      await requirePermission(opts.values ? 'secrets:read' : 'secrets:list');
      const ctx = resolveContext(opts);
      const scope = { organisation: ctx.org, workspace: ctx.workspace, environment: ctx.env };
      const client = getClient();

      if (!existsSync(opts.file)) {
        output.warn(`File ${chalk.bold(opts.file)} not found.`);
        process.exit(1);
      }

      const content = readFileSync(opts.file, 'utf-8');
      const local = parseEnvFile(content);

      progress.start('Comparing...');

      const remoteKeys = new Set<string>();
      const diffPage = await client.secrets.list(scope);
      for await (const secret of diffPage) {
        remoteKeys.add(secret.key);
      }

      progress.stop();

      const onlyLocal: string[] = [];
      const onlyRemote: string[] = [];
      const common: string[] = [];

      for (const key of local.keys()) {
        if (remoteKeys.has(key)) common.push(key);
        else onlyLocal.push(key);
      }
      for (const key of remoteKeys) {
        if (!local.has(key)) onlyRemote.push(key);
      }

      // Values are only compared on request: it costs one reveal per shared
      // key, each of which is a rate-limited, audit-logged decryption.
      const changed: string[] = [];
      if (opts.values && common.length > 0) {
        progress.start(`Comparing ${common.length} value(s)...`);
        try {
          const remote = await revealSecrets(client, scope, common);
          for (const key of common) {
            const value = remote.get(key);
            if (value !== undefined && value !== local.get(key)) changed.push(key);
          }
        } finally {
          progress.stop();
        }
      }

      const identical = onlyLocal.length === 0 && onlyRemote.length === 0 && changed.length === 0;

      if (identical) {
        // Never claim "in sync" off a key comparison alone. Matching key names
        // say nothing about values, and reporting that as in sync is how a
        // stale .env reaches production with the CLI's blessing.
        if (opts.values) output.success('Local and remote match (keys and values).');
        else output.success(`Keys match. Values not compared - pass ${chalk.bold('--values')} to compare them.`);
        return;
      }

      console.log();
      if (onlyLocal.length > 0) {
        console.log(chalk.green(`  + ${onlyLocal.length} only in local (${opts.file}):`));
        onlyLocal.forEach((k) => console.log(chalk.green(`    + ${k}`)));
      }
      if (onlyRemote.length > 0) {
        console.log(chalk.red(`  - ${onlyRemote.length} only in remote:`));
        onlyRemote.forEach((k) => console.log(chalk.red(`    - ${k}`)));
      }
      if (changed.length > 0) {
        // Key names only. Printing either side's value here would leak a
        // secret into a terminal and any CI log scraping this output.
        console.log(chalk.yellow(`  ~ ${changed.length} differ in value:`));
        changed.forEach((k) => console.log(chalk.yellow(`    ~ ${k}`)));
      }
      const unchanged = common.length - changed.length;
      if (unchanged > 0) {
        console.log(
          opts.values
            ? chalk.dim(`  = ${unchanged} identical`)
            : chalk.dim(`  = ${unchanged} in both (values not compared - pass --values)`),
        );
      }
      console.log();

      if (opts.exitCode) process.exit(1);
    } catch (err) {
      output.handleError(err);
    }
  });

// ---------------------------------------------------------------------------
// `cf sync daemon` - long-running pull that watches a workspace + environment
// and rewrites a local .env file whenever the remote set of secrets changes.
//
// Why polling, not push: the API does not yet expose an SSE / webhook stream
// for arbitrary secret-set changes. Polling with bounded backoff keeps the
// pressure off the API and is honest about latency. Plan tracks an SSE
// upgrade as a follow-up; switching the loop body is the only change once
// the server endpoint ships.
// ---------------------------------------------------------------------------

const DAEMON_BASE_POLL_MS = 30_000;
const DAEMON_BACKOFF_CEILING_MS = 300_000;
const DAEMON_BACKOFF_MULTIPLIER = 2;
const DAEMON_NO_CHANGE_GRACE = 5; // polls before backoff kicks in
const DAEMON_JITTER_PCT = 0.1;

function jittered(ms: number, pct: number): number {
  const delta = ms * pct * (Math.random() * 2 - 1);
  return Math.max(1_000, Math.round(ms + delta));
}

function hashSecrets(secrets: Map<string, string>): string {
  const h = createHash('sha256');
  const keys = [...secrets.keys()].sort();
  for (const k of keys) h.update(`${k}=${secrets.get(k) ?? ''} `);
  return h.digest('hex');
}

async function pullSecretSet(
  client: ReturnType<typeof getClient>,
  scope: { organisation?: string; workspace: string; environment: string },
): Promise<Map<string, string>> {
  // The daemon polls on an interval, so this is the difference between one
  // request per poll and one per secret per poll.
  return revealSecrets(client, scope);
}

function writeAtomic(path: string, body: string): void {
  const tmp = `${path}.tmp-${process.pid}`;
  writeFileSync(tmp, body, { encoding: 'utf-8', mode: 0o600 });
  renameSync(tmp, path);
}

export const daemonCommand = new Command('daemon')
  .description('Run a polling sync loop that rewrites a local .env file on remote changes')
  .option('-o, --org <id>', 'Organisation ID')
  .option('-w, --workspace <slug>', 'Workspace slug')
  .option('-e, --env <slug>', 'Environment slug')
  .option('-f, --file <path>', 'Output file path', '.env')
  .option('--interval <seconds>', 'Base poll interval in seconds (>=5)', String(DAEMON_BASE_POLL_MS / 1000))
  .option('--once', 'Run a single sync pass and exit (CI-friendly)')
  .option('-q, --quiet', 'Only log when the file changes')
  .action(async (opts) => {
    try {
      await requirePermission('secrets:read');
      const ctx = resolveContext(opts);
      const scope = { organisation: ctx.org, workspace: ctx.workspace, environment: ctx.env };
      const client = getClient();
      const baseIntervalMs = Math.max(5_000, Number(opts.interval) * 1_000);
      if (!Number.isFinite(baseIntervalMs)) {
        output.handleError(new Error(`Invalid --interval; expected a number, got ${opts.interval}`));
        return;
      }

      // Signal handling: SIGTERM / SIGINT flips a stop flag and the loop
      // exits cleanly on the next iteration boundary so we never leave a
      // half-written file or a phantom HTTPS connection open.
      let stop = false;
      const onSignal = (sig: NodeJS.Signals) => {
        if (stop) return;
        stop = true;
        console.error(chalk.dim(`\n[daemon] received ${sig}, stopping after current pass...`));
      };
      process.on('SIGTERM', onSignal);
      process.on('SIGINT', onSignal);

      console.error(chalk.bold(`[daemon] syncing ${ctx.workspace}/${ctx.env} -> ${opts.file} every ${baseIntervalMs / 1000}s`));

      let lastHash: string | null = null;
      let consecutiveNoChange = 0;
      let backoffMs = baseIntervalMs;

      while (!stop) {
        const startedAt = Date.now();
        try {
          const secrets = await pullSecretSet(client, scope);
          const nextHash = hashSecrets(secrets);

          if (nextHash !== lastHash) {
            const body = formatEnvFile(secrets);
            writeAtomic(opts.file, body);
            const action = lastHash === null ? 'wrote' : 'updated';
            console.error(chalk.green(`[daemon] ${action} ${opts.file} (${secrets.size} secrets)`));
            lastHash = nextHash;
            consecutiveNoChange = 0;
            backoffMs = baseIntervalMs;
          } else {
            consecutiveNoChange++;
            if (consecutiveNoChange >= DAEMON_NO_CHANGE_GRACE) {
              backoffMs = Math.min(backoffMs * DAEMON_BACKOFF_MULTIPLIER, DAEMON_BACKOFF_CEILING_MS);
            }
            if (!opts.quiet) {
              console.error(chalk.dim(`[daemon] no change (${secrets.size} secrets, next poll ${Math.round(backoffMs / 1000)}s)`));
            }
          }
        } catch (err) {
          // Reset backoff on transient API errors so we don't compound a
          // bad-network minute into a 5-minute lull. Keep going - the
          // daemon's job is to survive blips.
          consecutiveNoChange = 0;
          backoffMs = baseIntervalMs;
          console.error(chalk.red(`[daemon] pull failed: ${(err as Error).message ?? err}`));
        }

        if (opts.once || stop) break;

        const sleepMs = jittered(backoffMs, DAEMON_JITTER_PCT);
        await new Promise<void>((resolve) => {
          const t = setTimeout(resolve, sleepMs);
          // Wake immediately when a stop signal lands mid-sleep so SIGTERM
          // does not block for `backoffMs` before exiting.
          const onWake = () => { clearTimeout(t); resolve(); };
          process.once('SIGTERM', onWake);
          process.once('SIGINT', onWake);
        });
        void startedAt;
      }

      console.error(chalk.dim('[daemon] stopped'));
    } catch (err) {
      output.handleError(err);
    }
  });
